import PaginatedTable from '../../components/ui/PaginatedTable';
import PermissionButton from "../../components/ui/PermissionButton";
import { useParams } from "react-router-dom";
import DashboardLayout from "../Layouts/DashboardLayout";
import { useEffect, useState } from "react";

const tabs = [
  "Overview",
  "Inventory",
  "Inward",
  "Outward",
  "Sales",
  "Profit & Loss",
];

export default function BranchOverview() {
  const { branchId } = useParams();
  const [activeTab, setActiveTab] = useState("Overview");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReport();
  }, [branchId]);

  const loadReport = async () => {
    try {
      const res = await fetch(
        `/api/branch-reports/${branchId}`,
        {
          credentials: "include",
        },
      );

      const data = await res.json();

      if (data.status === "success") {
        setReport(data);
      } else {
        alert(data.message);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-10">Loading...</div>
      </DashboardLayout>
    );
  }

  if (!report) return null;

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-serif">{report.branch.name}</h1>
          <p className="text-sm text-gray-400">
            Complete branch performance & operations
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 bg-black/40 p-1 rounded-xl w-fit">
          {tabs.map((t) => (
            <PermissionButton permissionModule="branches"
              key={t}
              onClick={() => setActiveTab(t)}
              className={`px-5 py-2 text-sm rounded-lg transition ${
                activeTab === t
                  ? "bg-amber-400 text-black font-medium"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              {t}
            </PermissionButton>
          ))}
        </div>

        {/* Content */}
        <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
          {activeTab === "Overview" && <Overview overview={report.overview} />}
          {activeTab === "Inventory" && (
            <Table
              headers={["Item", "Stock", "Status"]}
              rows={report.inventory.map((i) => [
                i.item,
                `${i.stock} ${i.unit}`,
                <StatusBadge status={i.status} />,
              ])}
            />
          )}
          {activeTab === "Inward" && (
            <Table
              headers={["Date", "Item", "Qty", "Supplier"]}
              rows={report.inward.map((i) => [
                i.date,
                i.item,
                i.qty,
                i.supplier,
              ])}
            />
          )}
          {activeTab === "Outward" && (
            <Table
              headers={["Date", "Item", "Qty", "Purpose"]}
              rows={report.outward.map((i) => [
                i.date,
                i.item,
                i.qty,
                i.purpose,
              ])}
            />
          )}
          {activeTab === "Sales" && (
            <Table
              headers={["Item", "Category", "Revenue"]}
              rows={report.sales.map((i) => [
                i.item,
                i.category,
                `₹${i.revenue.toLocaleString()}`,
              ])}
            />
          )}
          {activeTab === "Profit & Loss" && <PL profit={report.profit_loss} />}
        </div>
      </div>
    </DashboardLayout>
  );
}

function Overview({ overview }) {
  const cards = [
    {
      label: "Revenue",
      value: `₹${overview.revenue.toLocaleString()}`,
    },
    {
      label: "Orders",
      value: overview.orders,
    },
    {
      label: "Net Profit",
      value: `₹${overview.net_profit.toLocaleString()}`,
    },
    {
      label: "Inventory",
      value: overview.inventory_health,
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
      {cards.map((c) => (
        <div
          key={c.label}
          className="rounded-xl bg-black/40 border border-white/10 p-5"
        >
          <p className="text-xs uppercase text-gray-400">{c.label}</p>

          <p className="mt-2 text-xl font-serif">{c.value}</p>
        </div>
      ))}
    </div>
  );
}

function PL({ profit }) {
  const cards = [
    {
      label: "Revenue",
      value: profit.revenue,
    },
    {
      label: "Raw Material",
      value: profit.raw_material,
    },
    {
      label: "Payroll",
      value: profit.payroll,
    },
    {
      label: "Net Profit",
      value: profit.profit,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-6">
      {cards.map((c) => (
        <div
          key={c.label}
          className="rounded-xl bg-black/40 border border-white/10 p-5"
        >
          <p className="text-xs uppercase text-gray-400">{c.label}</p>

          <p className="mt-2 text-xl font-serif">₹{c.value.toLocaleString()}</p>
        </div>
      ))}
    </div>
  );
}

function Table({ headers, rows }) {
  return (
    <div className="overflow-hidden rounded-xl border border-white/10">
      <PaginatedTable className="w-full text-sm">
        <thead className="bg-black/50 text-gray-400">
          <tr>
            {headers.map((h) => (
              <th key={h} className="px-5 py-3 text-left">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-white/10 hover:bg-white/5">
              {r.map((c, j) => (
                <td key={j} className="px-5 py-3">
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </PaginatedTable>
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span
      className={`px-3 py-1 rounded-full text-xs ${
        status === "OK"
          ? "bg-emerald-500/20 text-emerald-400"
          : "bg-red-500/20 text-red-400"
      }`}
    >
      {status}
    </span>
  );
}
